import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  NUMEROLOGY_BRAND_LOGO_URL,
  NUMEROLOGY_BRAND_TITLE,
  NUMEROLOGY_HOME_PATH,
} from './numerologyShellConstants';
import './NumerologyAccessGate.css';

async function fetchAccessStatus() {
  const r = await fetch('/api/numerology-access', {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!r.ok) return false;
  const data = await r.json().catch(() => ({}));
  return data.ok === true;
}

/**
 * Numerology access UI + server cookie check. Password is verified only via POST /api/numerology-unlock.
 */
export default function NumerologyAccessGate({ children }) {
  const [status, setStatus] = useState('loading'); // 'loading' | 'locked' | 'unlocked'
  const [password, setPassword] = useState('');
  const [showError, setShowError] = useState(false);

  const refresh = useCallback(async () => {
    const ok = await fetchAccessStatus();
    setStatus(ok ? 'unlocked' : 'locked');
    return ok;
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setShowError(false);
    try {
      const r = await fetch('/api/numerology-unlock', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (r.ok) {
        setPassword('');
        setStatus('unlocked');
        return;
      }
      setShowError(true);
    } catch {
      setShowError(true);
    }
  };

  if (status === 'loading') {
    return (
      <div className="amm-num-access-gate amm-num-access-gate--loading" aria-busy="true">
        <p className="amm-num-access-gate__loading-text">Checking access…</p>
      </div>
    );
  }

  if (status === 'unlocked') {
    return children;
  }

  return (
    <div className="amm-num-access-gate">
      <Link className="amm-num-access-gate__brand" to={NUMEROLOGY_HOME_PATH}>
        <img
          className="amm-num-access-gate__logo"
          src={NUMEROLOGY_BRAND_LOGO_URL}
          alt="Astral Moon Magic"
          decoding="async"
        />
        <span className="amm-num-access-gate__brand-text">{NUMEROLOGY_BRAND_TITLE}</span>
      </Link>

      <div className="amm-num-access-gate__card">
        <h1 className="amm-num-access-gate__title">Enter your course password</h1>
        <p className="amm-num-access-gate__lead">
          This private Numerology area is available to paying customers. Enter your password to continue.
        </p>
        <p className="amm-num-access-gate__sharing">
          This private course area is for customers only. Sharing access is prohibited.
        </p>

        <form onSubmit={onSubmit} noValidate>
          {showError ? (
            <p className="amm-num-access-gate__error" role="alert">
              That password is not correct. Please try again or contact support if you need access.
            </p>
          ) : null}

          <div className="amm-num-access-gate__field">
            <label className="amm-num-access-gate__label" htmlFor="amm-numerology-access-password">
              Password
            </label>
            <input
              id="amm-numerology-access-password"
              className={`amm-num-access-gate__input${showError ? ' amm-num-access-gate__input--error' : ''}`}
              type="password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(ev) => {
                setPassword(ev.target.value);
                if (showError) setShowError(false);
              }}
            />
          </div>

          <div className="amm-num-access-gate__actions">
            <button type="submit" className="amm-button">
              Unlock
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
